"use client";

// Step 6.7.2 / AC164: URL <-> store sync hook.
//
// One mount-time hydration step + one effect that pushes store changes
// back to the URL (which triggers Next.js server re-render with the new
// query params, which is how the catalog grid actually re-filters).
//
// Components must call this once per page mount. Multiple calls in the
// same tree are safe but wasteful; co-locate it at the catalog page
// boundary.

import { useEffect, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  filtersFromSearchParams,
  filtersToSearchParams,
  pickFilters,
  useCatalogFilters,
} from "./catalog-filters";

export function useCatalogFiltersSync(): void {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hydrated = useRef(false);
  const hydrateFromUrl = useCatalogFilters((s) => s.hydrateFromUrl);
  // Subscribe to each primitive / array reference individually. Object-
  // literal selectors return a fresh reference each render and trigger
  // an infinite loop under Zustand v5's Object.is equality check.
  const categories = useCatalogFilters((s) => s.categories);
  const colors = useCatalogFilters((s) => s.colors);
  const lengths = useCatalogFilters((s) => s.lengths);
  const price = useCatalogFilters((s) => s.price);
  const sort = useCatalogFilters((s) => s.sort);

  // Mount-time hydration. Reads URL once and seeds the store. After
  // this fires, the store is the canonical reader and writes drive
  // the URL (not the other way around).
  /* eslint-disable react-hooks/exhaustive-deps -- one-shot URL->store hydration on mount, dependency-free */
  useEffect(() => {
    if (hydrated.current) return;
    hydrateFromUrl(searchParams);
    hydrated.current = true;
  }, []);

  // Store -> URL sync. After hydration, every store change writes the
  // serialized snapshot back to the URL via router.push (so the server
  // component refilters). Skip until hydration is done so we don't
  // immediately overwrite an external URL change with the empty store.
  //
  // #532: read the LIVE store (getState), not this render's captured values.
  // On first load both effects run in the same commit, so the captured values
  // are still the empty pre-hydration store; using them pushed the bare
  // pathname and wiped a deep-linked filter (?category=rose) ~2s after load.
  // Compare canonical forms (same serializer on both sides) so a URL that
  // already says what the store says never navigates.
  useEffect(() => {
    if (!hydrated.current) return;
    const preserve = new URLSearchParams(searchParams);
    const nextQs = filtersToSearchParams(
      pickFilters(useCatalogFilters.getState()),
      preserve,
    ).toString();
    const currentQs = filtersToSearchParams(
      filtersFromSearchParams(preserve),
      preserve,
    ).toString();
    if (nextQs === currentQs) return;
    router.push(nextQs ? `${pathname}?${nextQs}` : pathname);
    router.refresh();
  }, [categories, colors, lengths, price, sort]);
  /* eslint-enable react-hooks/exhaustive-deps */
}
